import { Email, WhatsApp, MoreHorizOutlined } from "@mui/icons-material";
import GppGoodIcon from "@mui/icons-material/GppGood";
import {
  IconButton,
  Popover,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
} from "@mui/material";
import { Link01 } from "@untitled-ui/icons-react";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import React from "react";
import toast from "react-hot-toast";
import { IApprovalStageCustomer } from "src/services/thumbz-base-api";
import { getLinkToApprove } from "src/utils/get-link-to-approve";
import { useAppSelector } from "src/app/hooks";
import { approvalEditSlice } from "src/features/approval-edit/approvalEditSlice";
import {
  IApprovalItemRevisionAirStatusEnum,
  IStatusTypeStpTypeEnum,
} from "src/services/thumbz-base-api";
import { useSelector } from "react-redux";
import { authSlice } from "src/features/auth/authSlice";
import { useUserRole } from "src/hooks/use-user-roles";
import { ForceApprovalModal } from "src/sections/approval-edit/force-approval-modal";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "src/config/query-key";
import { useThumbzApi } from "src/services/thumbz-api";
import { useOrganization } from "src/contexts/organization/OrganizationContext";
import { useMounted } from "src/hooks/use-mounted";

interface ICustomerPopupMenuProps {
  approvalStageCustomer: IApprovalStageCustomer;
  wrk_slug: string;
}

export const CustomerPopupMenu: React.FC<ICustomerPopupMenuProps> = ({
  approvalStageCustomer,
  wrk_slug,
}) => {
  const approval = useAppSelector(approvalEditSlice.selectors.approval);
  const loggedUser = useSelector(authSlice.selectors.selectUser);
  const { api } = useThumbzApi();
  const { selectedOrganization } = useOrganization();
  const isMounted = useMounted();
  const orgId = selectedOrganization?.org_id ?? "";
  const collaboratorsQuery = useQuery({
    queryKey: queryKeys.organization.collaborators({ org_id: orgId }).queryKey,
    queryFn: () => api.organization.organizationControllerGetCollaborators({ orgId }),
    enabled: Boolean(orgId) && isMounted(),
  });
  const collaborators = collaboratorsQuery.data ?? [];
  const { isOwner, isMember } = useUserRole(
    collaborators,
    loggedUser?.usu_id || null,
    loggedUser?.usu_id || "",
  );

  const [forceOpen, setForceOpen] = React.useState<boolean>(false);

  function handleCopyLink() {
    if (!approvalStageCustomer?.asc_id) {
      toast.error(
        "Não foi possível copiar o link | " + wrk_slug + " " + approvalStageCustomer?.asc_id,
      );
      return;
    }

    navigator.clipboard.writeText(
      getLinkToApprove({ asc_id: approvalStageCustomer.asc_id, wrk_slug }),
    );
    toast.success("Link copiado");
  }

  function handleResendEmail() {
    alert("Email reenviado");
  }

  function handleResendWhatsApp() {
    alert("WhatsApp reenviado");
  }

  const hasPending = approvalStageCustomer.revisions.some(
    (r) => r.air_status === IApprovalItemRevisionAirStatusEnum.Pending,
  );
  const hasRequested = approvalStageCustomer.revisions.some(
    (r) => r.air_status === IApprovalItemRevisionAirStatusEnum.Requested,
  );
  const inReview = approval?.apv_status.statusType.stp_type === IStatusTypeStpTypeEnum.IN_REVIEW;
  const notApproved = approvalStageCustomer.asc_approved === false;
  const canForceApprove = Boolean(
    (isOwner || isMember) && inReview && notApproved && hasPending && !hasRequested,
  );

  const disabledReason = !inReview
    ? "Status não está em revisão"
    : approval?.apv_deleted_at
      ? "Aprovação arquivada"
      : !notApproved
        ? "Aprovador já concluiu a aprovação"
        : hasRequested
          ? "Há revisões com solicitação de alterações"
          : !hasPending
            ? "Não existem revisões pendentes"
            : undefined;

  return (
    <PopupState variant="popover" popupId="approval-customer-actions">
      {(popupState) => (
        <div>
          <IconButton
            {...bindTrigger(popupState)}
            sx={{
              p: 0,
            }}
          >
            <MoreHorizOutlined />
          </IconButton>
          <Popover
            {...bindPopover(popupState)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <Menu
              anchorEl={popupState.anchorEl}
              open={Boolean(popupState.anchorEl)}
              onClose={() => popupState.close()}
            >
              <MenuItem onClick={handleCopyLink}>
                <ListItemIcon>
                  <Link01 />
                </ListItemIcon>
                <ListItemText>Copiar link</ListItemText>
              </MenuItem>

              <MenuItem disabled onClick={handleResendEmail}>
                <ListItemIcon>
                  <Email />
                </ListItemIcon>
                <ListItemText>Reenviar Email</ListItemText>
              </MenuItem>

              <MenuItem disabled onClick={handleResendWhatsApp}>
                <ListItemIcon>
                  <WhatsApp />
                </ListItemIcon>
                <ListItemText>Reenviar WhatsApp</ListItemText>
              </MenuItem>

              <Divider />

              {(isOwner || isMember) && (
                <Tooltip title={disabledReason || "Forçar aprovação"} placement="top">
                  <span>
                    <MenuItem
                      disabled={!canForceApprove}
                      onClick={() => {
                        popupState.close();
                        setForceOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <GppGoodIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Forçar aprovação</ListItemText>
                    </MenuItem>
                  </span>
                </Tooltip>
              )}
            </Menu>
          </Popover>
          {forceOpen && (
            <ForceApprovalModal
              open={forceOpen}
              onClose={() => setForceOpen(false)}
              approvalStageCustomer={approvalStageCustomer}
            />
          )}
        </div>
      )}
    </PopupState>
  );
};
